import { useEffect, useMemo, useState } from "react";
import { Alert, Button, Form, Nav, Spinner, Tab } from "react-bootstrap";
import { useHistory, useParams } from "react-router-dom";

import { useGetAllCategories } from "../../../../../api/jeep-rally/categories/categories.hooks";
import {
  useAddRouteCheckpointMutation,
  useDeleteCheckpointMutation,
  useGetRouteCheckpoints,
} from "../../../../../api/jeep-rally/checkpoints/checkpoints.hooks";
import { useGetRallyById } from "../../../../../api/jeep-rally/rally/rally.hooks";
import { useGetRouteById } from "../../../../../api/jeep-rally/routes/routes.hooks";
import { useUploadFileMutation } from "../../../../../api/jeep-rally/uploads/uploads.hooks";
import {
  getCategoryTypeName,
  isJeepRallyAdmin,
  normalizeCategories,
} from "../../Categories/utils/category.utils";
import {
  getCategorySelectValue,
  getRallyApiErrorMessage,
  getRallyCategories,
  getRallyRouteId,
  normalizeRally,
} from "../utils/rally.utils";
import CheckpointCategoryList from "./_components/CheckpointCategoryList";
import CheckpointFormEditor from "./_components/CheckpointFormEditor";
import {
  categoryMatchesType,
  createEmptySlots,
  emptyCheckpointSlot,
  filterCheckpointsForStageCategory,
  getCheckpointApiErrorMessage,
  getEntityId,
  getEntityLabel,
  getStageLabel,
  getTypeTokens,
  mapFilteredCheckpointsToSlots,
  normalizeCheckpoints,
  saveStageCategoryCheckpoints,
} from "./utils/checkpointForm.utils";

const resolveRallyTypes = (rally) => {
  const raw = Array.isArray(rally?.types)
    ? rally.types
    : rally?.type
      ? [rally.type]
      : [];

  return raw
    .map((item) => {
      if (!item) return null;
      if (typeof item === "string") {
        return { _id: item, name: item, key: item };
      }
      return item;
    })
    .filter(Boolean);
};

const resolveRallyCategories = (rally, allCategories = []) => {
  const raw = getRallyCategories(rally);
  if (!raw.length) return [];

  return raw
    .map((item) => {
      if (item && typeof item === "object" && (item._id || item.id || item.key)) {
        const id = item._id || item.id;
        if (item.typeId || item.type) return item;
        const match = allCategories.find(
          (category) =>
            getEntityId(category) === id ||
            category.key === item.key ||
            category.key === getCategorySelectValue(item)
        );
        return match ? { ...match, ...item } : item;
      }

      const token = String(item || "").trim();
      if (!token) return null;
      return (
        allCategories.find(
          (category) =>
            getEntityId(category) === token || category.key === token
        ) || null
      );
    })
    .filter(Boolean);
};

const RallyCheckpointsPage = () => {
  const history = useHistory();
  const { id: rallyId } = useParams();
  const canManage = isJeepRallyAdmin();

  const [selectedTypeId, setSelectedTypeId] = useState("");
  const [activeStageId, setActiveStageId] = useState("");
  const [editorCategory, setEditorCategory] = useState(null);
  const [slots, setSlots] = useState(createEmptySlots());
  const [slotErrors, setSlotErrors] = useState({});
  const [formError, setFormError] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [submitSuccess, setSubmitSuccess] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const {
    data: rallyData,
    isLoading: isRallyLoading,
    isError: isRallyError,
    error: rallyError,
  } = useGetRallyById(rallyId);

  const rally = useMemo(() => normalizeRally(rallyData), [rallyData]);
  const routeId = useMemo(() => getRallyRouteId(rally), [rally]);
  const rallyTypes = useMemo(() => resolveRallyTypes(rally), [rally]);

  const { data: categoriesData, isLoading: isCategoriesLoading } =
    useGetAllCategories({ status: "active" });
  const allCategories = useMemo(
    () => normalizeCategories(categoriesData),
    [categoriesData]
  );

  const rallyCategories = useMemo(
    () => resolveRallyCategories(rally, allCategories),
    [rally, allCategories]
  );

  const {
    data: routeData,
    isLoading: isRouteLoading,
    isError: isRouteError,
    error: routeError,
  } = useGetRouteById(routeId, { enabled: Boolean(routeId) });

  const route = useMemo(() => {
    if (!routeData) return null;
    if (routeData._id || routeData.id || routeData.title) return routeData;
    return routeData.data || routeData.route || null;
  }, [routeData]);

  const stages = useMemo(() => {
    const list = Array.isArray(route?.stages) ? route.stages : [];
    return [...list].sort(
      (a, b) => Number(a.stage_number || 0) - Number(b.stage_number || 0)
    );
  }, [route]);

  const {
    data: checkpointsData,
    isLoading: isCheckpointsLoading,
    refetch: refetchCheckpoints,
  } = useGetRouteCheckpoints(routeId, {}, { enabled: Boolean(routeId) });

  const checkpoints = useMemo(
    () => normalizeCheckpoints(checkpointsData),
    [checkpointsData]
  );

  const { mutateAsync: createCheckpoint } = useAddRouteCheckpointMutation();
  const { mutateAsync: deleteCheckpoint } = useDeleteCheckpointMutation();
  const { mutateAsync: uploadFile } = useUploadFileMutation();

  useEffect(() => {
    if (!rallyTypes.length) {
      setSelectedTypeId("");
      return;
    }
    if (rallyTypes.length === 1) {
      setSelectedTypeId(getEntityId(rallyTypes[0]) || getTypeTokens(rallyTypes[0])[0] || "");
      return;
    }
    setSelectedTypeId((prev) => {
      if (!prev) return "";
      const stillValid = rallyTypes.some((type) =>
        getTypeTokens(type).includes(String(prev))
      );
      return stillValid ? prev : "";
    });
  }, [rallyTypes]);

  useEffect(() => {
    if (!stages.length) {
      setActiveStageId("");
      return;
    }
    setActiveStageId((prev) => {
      if (prev && stages.some((stage) => getEntityId(stage) === prev)) {
        return prev;
      }
      return getEntityId(stages[0]);
    });
  }, [stages]);

  const selectedType = useMemo(
    () =>
      rallyTypes.find((type) =>
        getTypeTokens(type).includes(String(selectedTypeId))
      ) || null,
    [rallyTypes, selectedTypeId]
  );

  const activeStage = useMemo(
    () => stages.find((stage) => getEntityId(stage) === activeStageId) || null,
    [stages, activeStageId]
  );

  const filteredCategories = useMemo(() => {
    if (!selectedTypeId) return [];
    return rallyCategories.filter((category) =>
      categoryMatchesType(category, selectedTypeId)
    );
  }, [rallyCategories, selectedTypeId]);

  const goBack = () => history.push("/jeep-rally/rallies");

  const openEditor = (category) => {
    const categoryId = getEntityId(category);
    const existing = filterCheckpointsForStageCategory(
      checkpoints,
      activeStageId,
      categoryId
    );
    setEditorCategory(category);
    setSlots(mapFilteredCheckpointsToSlots(existing));
    setSlotErrors({});
    setFormError("");
    setSubmitError("");
    setSubmitSuccess("");
  };

  const closeEditor = () => {
    setEditorCategory(null);
    setSlots(createEmptySlots());
    setSlotErrors({});
    setFormError("");
  };

  const updateSlot = (order, patch) => {
    setSlots((prev) =>
      prev.map((slot) => (slot.order === order ? { ...slot, ...patch } : slot))
    );
    setSlotErrors((prev) => {
      const next = { ...prev };
      delete next[order];
      return next;
    });
    setFormError("");
    setSubmitSuccess("");
  };

  const clearSlot = (order) => {
    setSlots((prev) =>
      prev.map((slot) =>
        slot.order === order ? emptyCheckpointSlot(order) : slot
      )
    );
    setSlotErrors((prev) => {
      const next = { ...prev };
      delete next[order];
      return next;
    });
  };

  const handleSave = async () => {
    if (!canManage || !routeId || !activeStageId || !editorCategory) return;

    const categoryId = getEntityId(editorCategory);
    const typeId =
      getEntityId(selectedType) ||
      getTypeTokens(selectedType)[0] ||
      selectedTypeId;

    setIsSaving(true);
    setSubmitError("");
    setSubmitSuccess("");
    setFormError("");
    setSlotErrors({});

    const existing = filterCheckpointsForStageCategory(
      checkpoints,
      activeStageId,
      categoryId
    );

    try {
      await saveStageCategoryCheckpoints({
        routeId,
        stageId: activeStageId,
        categoryId,
        eventId: rallyId,
        typeId,
        slots,
        existingCheckpoints: existing,
        deleteCheckpoint,
        createCheckpoint,
        uploadFile,
      });

      setSubmitSuccess("Checkpoints saved successfully.");
      await refetchCheckpoints();
      closeEditor();
    } catch (error) {
      if (error?.validationErrors) {
        setFormError(error.validationErrors.form || "");
        setSlotErrors(error.validationErrors.slots || {});
      } else {
        setSubmitError(
          getCheckpointApiErrorMessage(error, "Failed to save checkpoints.")
        );
      }
    } finally {
      setIsSaving(false);
    }
  };

  if (isRallyLoading || isCategoriesLoading) {
    return (
      <div className="container-fluid">
        <div className="card">
          <div className="card-body">
            <div className="d-flex justify-content-center py-5">
              <Spinner animation="border" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isRallyError || !rally) {
    return (
      <div className="container-fluid">
        <div className="card">
          <div className="card-body">
            <Alert variant="danger" className="mb-0">
              {getRallyApiErrorMessage(
                rallyError,
                "Rally not found. Open it from the rallies list."
              )}
            </Alert>
            <Button variant="outline-secondary" className="mt-3" onClick={goBack}>
              Back to rallies
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const editorContext = editorCategory
    ? {
        stageLabel: getStageLabel(
          activeStage,
          stages.findIndex((stage) => getEntityId(stage) === activeStageId)
        ),
        typeLabel: getEntityLabel(selectedType) || selectedTypeId || "-",
        categoryLabel:
          getEntityLabel(editorCategory) ||
          editorCategory.title ||
          getCategoryTypeName(editorCategory) ||
          "-",
      }
    : null;

  const loadingList =
    Boolean(routeId) && (isRouteLoading || isCheckpointsLoading);

  return (
    <div className="container-fluid">
      <div className="card">
        <div className="card-header d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center gap-2">
            <button
              type="button"
              className="btn btn-link p-0 text-decoration-none"
              onClick={editorCategory ? closeEditor : goBack}
              aria-label={editorCategory ? "Back to categories" : "Back to rallies"}
              title="Back"
            >
              <i className="bi bi-arrow-left fs-4" />
            </button>
            <div>
              <h4 className="card-title mb-0">Rally Checkpoints</h4>
              <div className="text-muted small">{rally.name || rallyId}</div>
            </div>
          </div>
        </div>

        <div className="card-body">
          {!canManage && (
            <Alert variant="warning" className="mb-4">
              Admin access is required to edit checkpoints.
            </Alert>
          )}

          {submitError && (
            <Alert variant="danger" className="mb-4">
              {submitError}
            </Alert>
          )}

          {submitSuccess && !editorCategory && (
            <Alert variant="success" className="mb-4">
              {submitSuccess}
            </Alert>
          )}

          {!routeId ? (
            <Alert variant="warning" className="mb-0">
              This rally has no route assigned. Edit the rally and select a route
              first.
            </Alert>
          ) : isRouteError ? (
            <Alert variant="danger" className="mb-0">
              {getRallyApiErrorMessage(
                routeError,
                "Failed to load the rally route and stages."
              )}
            </Alert>
          ) : editorCategory ? (
            <CheckpointFormEditor
              context={editorContext}
              slots={slots}
              slotErrors={slotErrors}
              formError={formError}
              submitting={isSaving}
              readOnly={!canManage}
              onSlotChange={updateSlot}
              onClearSlot={clearSlot}
              onSubmit={handleSave}
              onBack={closeEditor}
            />
          ) : (
            <>
              <p className="text-muted mb-4">
                Select a type, then a stage tab, then open a category to edit up
                to 4 checkpoints for that stage and category.
              </p>

              {!rallyTypes.length ? (
                <Alert variant="info" className="mb-0">
                  This rally has no types assigned. Edit the rally and select
                  types first.
                </Alert>
              ) : (
                <>
                  <Form.Group className="mb-4" style={{ maxWidth: 420 }}>
                    <Form.Label>
                      Type <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Select
                      value={selectedTypeId}
                      onChange={(e) => {
                        setSelectedTypeId(e.target.value);
                        setSubmitSuccess("");
                      }}
                      disabled={isSaving || rallyTypes.length === 1}
                    >
                      {rallyTypes.length > 1 ? (
                        <option value="">Select a type</option>
                      ) : null}
                      {rallyTypes.map((type) => {
                        const typeId =
                          getEntityId(type) || getTypeTokens(type)[0];
                        return (
                          <option key={typeId} value={typeId}>
                            {getEntityLabel(type) || typeId}
                          </option>
                        );
                      })}
                    </Form.Select>
                    <Form.Text className="text-muted">
                      Only types assigned to this rally are shown. Exactly one
                      type must be selected.
                    </Form.Text>
                  </Form.Group>

                  {!selectedTypeId ? (
                    <Alert variant="info" className="mb-0">
                      Select a type to view stages and categories.
                    </Alert>
                  ) : loadingList ? (
                    <div className="d-flex justify-content-center py-5">
                      <Spinner animation="border" />
                    </div>
                  ) : !stages.length ? (
                    <Alert variant="info" className="mb-0">
                      No stages found on this rally&apos;s route. Add stages to
                      the route first.
                    </Alert>
                  ) : (
                    <Tab.Container
                      activeKey={activeStageId}
                      onSelect={(key) => {
                        if (!key) return;
                        setActiveStageId(key);
                        setSubmitSuccess("");
                      }}
                    >
                      <Nav variant="tabs" className="mb-3">
                        {stages.map((stage, index) => {
                          const stageId = getEntityId(stage);
                          return (
                            <Nav.Item key={stageId || index}>
                              <Nav.Link eventKey={stageId}>
                                {getStageLabel(stage, index)}
                              </Nav.Link>
                            </Nav.Item>
                          );
                        })}
                      </Nav>

                      <Tab.Content>
                        {stages.map((stage, index) => {
                          const stageId = getEntityId(stage);
                          return (
                            <Tab.Pane key={stageId || index} eventKey={stageId}>
                              <div className="mb-3 text-muted small">
                                Route: {route?.title || route?.name || routeId}
                              </div>
                              <CheckpointCategoryList
                                categories={filteredCategories}
                                checkpoints={checkpoints}
                                activeStageId={stageId}
                                onSelectCategory={openEditor}
                                disabled={isSaving || !canManage}
                              />
                            </Tab.Pane>
                          );
                        })}
                      </Tab.Content>
                    </Tab.Container>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default RallyCheckpointsPage;
